import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const DocumentEditor = ({ isOpen, onClose, document, onSave, title, isReadOnly }) => {
  const [jsonContent, setJsonContent] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (document) {
      // Remove _id when editing since it can't be modified
      const { _id, ...docWithoutId } = document;
      setJsonContent(JSON.stringify(docWithoutId, null, 2));
    } else {
      // For new documents, start with empty object
      setJsonContent('{\n  \n}');
    }
    setError('');
  }, [document]);

  const handleSave = () => {
    try {
      const parsed = JSON.parse(jsonContent);
      onSave(parsed);
      onClose();
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-3xl h-[90vh] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h3 className="text-lg font-medium text-ui-text-primary">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-hidden flex flex-col">
          <div className="flex-1 relative">
            <textarea
              value={jsonContent}
              onChange={(e) => {
                setJsonContent(e.target.value);
                setError('');
              }}
              className="document-editor-textarea w-full h-full p-3 font-mono text-sm bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-600 rounded text-ui-text-primary resize-none focus:outline-none focus:ring-2 focus:ring-neutral-300 dark:focus:ring-neutral-600 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder="Enter JSON document..."
              spellCheck={false}
              disabled={isReadOnly}
              title={isReadOnly ? 'Upgrade to edit documents' : ''}
            />
          </div>
          {error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isReadOnly}
            className={`px-4 py-2 text-sm rounded-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${
              isReadOnly
                ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
                : 'bg-brand-primary text-black hover:opacity-90'
            }`}
            title={isReadOnly ? 'Upgrade to save documents' : ''}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentEditor; 